<template>
    <div>
        <h4 class="text-slate-800 dark:text-slate-100 text-xl font-semibold">Passkeys</h4>
        <p class="text-slate-500 dark:text-slate-400 text-sm mb-8">
            Passkeys let you sign in quickly and securely using your face, fingerprint, or screen lock.
            <br />
            You can add multiple passkeys for different devices.
        </p>

        <!-- Browser Support Warning -->
        <div
            v-if="!isSupported"
            class="p-4 mb-6 bg-yellow-50 border border-yellow-200 rounded-lg"
        >
            <div class="flex items-start">
                <svg
                    class="w-5 h-5 text-yellow-600 mt-0.5 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                >
                    <path
                        fill-rule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                    />
                </svg>
                <div>
                    <p class="text-sm font-medium text-yellow-800">
                        Passkeys are not supported in this browser
                    </p>
                    <p class="text-sm text-yellow-700 mt-1">
                        Please use Chrome, Safari, Firefox, or Edge to register passkeys.
                    </p>
                </div>
            </div>
        </div>

        <!-- Passkey List -->
        <div v-if="isSupported && passkeys.length > 0" class="mt-3 mb-6">
            <h5 class="text-slate-800 dark:text-slate-100 text-sm font-medium mb-3">Your Passkeys</h5>
            <div class="overflow-hidden">
                <div
                    v-for="(passkey, index) in passkeys"
                    :key="passkey.id"
                    class="flex py-3 px-0 items-center gap-6 self-stretch"
                    :class="{
                        'border-b border-slate-200 dark:border-slate-700': index < passkeys.length - 1,
                    }"
                >
                    <div class="flex items-center gap-3 flex-1">
                        <div class="flex p-3 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                                />
                            </svg>
                        </div>
                        <div class="flex flex-col items-start gap-1 flex-1">
                            <span class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100">
                                {{ passkey.name || 'Unnamed Passkey' }}
                            </span>
                            <span class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400">
                                Last used: {{ formatDate(passkey.last_used_at) }}
                            </span>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <button
                            @click="startRename(passkey)"
                            class="flex items-center gap-2 px-2 py-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 rounded"
                            title="Rename"
                        >
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                            </svg>
                        </button>
                        <button
                            @click="confirmDelete(passkey)"
                            class="flex items-center gap-2 px-2 py-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 rounded"
                            title="Delete"
                        >
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Passkey Button -->
        <div v-if="isSupported">
            <ActionButton
                v-if="!showRegisterForm"
                @clicked="showRegisterForm = true"
                label="Add Passkey"
                classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium"
            />

            <!-- Register Form -->
            <div v-else class="w-full p-6 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <h5 class="text-slate-800 dark:text-slate-100 text-base font-medium mb-4">Add New Passkey</h5>
                <p class="text-sm text-gray-500 dark:text-slate-400 mb-4">
                    Give your passkey a name to help you remember which device it's for.
                </p>
                <input
                    type="text"
                    :class="[
                        'w-60 form-input placeholder:text-left mb-4',
                        errors.length > 0 ? '!border-red-500' : ''
                    ]"
                    v-model="passkeyName"
                    placeholder="e.g., iPhone, MacBook Pro"
                />
                <Errors :errors="errors" class="mb-4" />
                <div class="flex items-center gap-4">
                    <ActionButton
                        @clicked="registerPasskey"
                        label="Create Passkey"
                        :loading="loading"
                        classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium"
                    />
                    <button
                        class="flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 text-slate-800 text-sm font-medium hover:bg-slate-50 transition-colors"
                        @click="cancelRegister"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>

        <!-- Rename Modal -->
        <div
            v-if="renamePasskey"
            class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            @click.self="renamePasskey = null"
        >
            <div class="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
                <h5 class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">Rename Passkey</h5>
                <input
                    type="text"
                    class="w-full form-input mb-4"
                    v-model="newPasskeyName"
                    placeholder="Enter new name"
                    @keyup.enter="handleRename"
                />
                <div class="flex justify-end gap-3">
                    <button
                        @click="renamePasskey = null"
                        class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
                    >
                        Cancel
                    </button>
                    <ActionButton
                        @clicked="handleRename"
                        label="Save"
                        :loading="loading"
                        classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium"
                    />
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div
            v-if="deletePasskey"
            class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            @click.self="deletePasskey = null"
        >
            <div class="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4">
                <h5 class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Delete Passkey?</h5>
                <p class="text-sm text-slate-600 dark:text-slate-300 mb-6">
                    Are you sure you want to delete "{{ deletePasskey.name || 'Unnamed Passkey' }}"?
                    This action cannot be undone.
                </p>
                <div class="flex justify-end gap-3">
                    <button
                        @click="deletePasskey = null"
                        class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
                    >
                        Cancel
                    </button>
                    <ActionButton
                        @clicked="handleDelete"
                        label="Delete"
                        :loading="loading"
                        classes="bg-red-600 hover:bg-red-700 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { usePasskey } from '@/composables/usePasskey';
import { usePage } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import ActionButton from '@/components/action-button.vue';
import Errors from '@/components/errors.vue';
import { Apii } from '@/services/api.js';

const ApiService = new Apii();
const { isSupported, register, deletePasskey: deletePasskeyFn, renamePasskey: renamePasskeyFn, loading, error } = usePasskey();
const page = usePage();

const passkeys = ref([]);
const showRegisterForm = ref(false);
const passkeyName = ref('');
const renamePasskey = ref(null);
const newPasskeyName = ref('');
const deletePasskey = ref(null);
const errors = ref([]);

const loadPasskeys = async () => {
    try {
        const response = await ApiService.apiCall('/user/passkeys', 'get');
        passkeys.value = response.data.passkeys || [];
    } catch (err) {
        console.error('Failed to load passkeys:', err);
        notify(
            {
                group: 'error',
                title: 'Failed to Load Passkeys',
                text: 'Unable to fetch your passkeys. Please refresh the page.',
            },
            4000
        );
    }
};

// Check for duplicate names as user types
watch(passkeyName, (newName) => {
    if (!newName.trim()) {
        errors.value = [];
        return;
    }
    
    const isDuplicate = passkeys.value.some(
        passkey => passkey.name.toLowerCase() === newName.trim().toLowerCase()
    );
    
    if (isDuplicate) {
        errors.value = [{ message: 'You already have a passkey with this name. Please choose a different name.' }];
    } else {
        errors.value = [];
    }
});

const registerPasskey = async () => {
    // Don't submit if there's a validation error
    if (errors.value.length > 0) {
        return;
    }
    
    errors.value = [];
    try {
        await register(passkeyName.value || null);
        notify(
            {
                group: 'success',
                title: 'Passkey Added',
                text: 'Your passkey has been successfully registered!',
            },
            4000
        );
        showRegisterForm.value = false;
        passkeyName.value = '';
        await loadPasskeys();
    } catch (err) {
        errors.value = [{ message: error.value || 'Failed to register passkey' }];
        notify(
            {
                group: 'error',
                title: 'Registration Failed',
                text: error.value || 'Failed to register passkey. Please try again.',
            },
            4000
        );
    }
};

const cancelRegister = () => {
    showRegisterForm.value = false;
    passkeyName.value = '';
    errors.value = [];
};

const startRename = (passkey) => {
    renamePasskey.value = passkey;
    newPasskeyName.value = passkey.name || '';
};

const handleRename = async () => {
    try {
        await renamePasskeyFn(renamePasskey.value.id, newPasskeyName.value);
        notify(
            {
                group: 'success',
                title: 'Passkey Renamed',
                text: 'Your passkey has been renamed successfully!',
            },
            4000
        );
        renamePasskey.value = null;
        newPasskeyName.value = '';
        await loadPasskeys();
    } catch (err) {
        notify(
            {
                group: 'error',
                title: 'Rename Failed',
                text: error.value || 'Failed to rename passkey. Please try again.',
            },
            4000
        );
    }
};

const confirmDelete = (passkey) => {
    deletePasskey.value = passkey;
};

const handleDelete = async () => {
    try {
        await deletePasskeyFn(deletePasskey.value.id);
        notify(
            {
                group: 'success',
                title: 'Passkey Deleted',
                text: 'Your passkey has been deleted successfully!',
            },
            4000
        );
        deletePasskey.value = null;
        await loadPasskeys();
    } catch (err) {
        notify(
            {
                group: 'error',
                title: 'Delete Failed',
                text: error.value || 'Failed to delete passkey. Please try again.',
            },
            4000
        );
    }
};

const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString();
};

onMounted(() => {
    loadPasskeys();
});
</script>
